import React, { useState, useRef, useEffect } from 'react';
import './MultipleChoiceExercise.css';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];
const OPTION_COUNTS = [2, 3, 4];

// Replace any run of underscores in user-typed sentences with the canonical
// `___` marker. Teachers don't all type the same number of underscores
// (we've seen `_`, `___`, `_____`); normalizing on save keeps the stored
// data consistent and the rendered blank always looks the same.
const normalizeBlanks = (text) => (typeof text === 'string' ? text.replace(/_+/g, '___') : text);

// Resize an options array to a new count: truncate from the tail when
// shrinking, pad with empty strings when growing. Returns a copy.
const resizeOptions = (options, count) => {
  const safe = Array.isArray(options) ? options : [];
  if (count <= safe.length) return safe.slice(0, count);
  return [...safe, ...Array(count - safe.length).fill('')];
};

const MCIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="8 12 11 15 16 9"/>
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);


function initAnswers(mode, sentences, gaps) {
  if (mode === 'sentences') {
    return Object.fromEntries((sentences || []).map(s => [s.id, null]));
  }
  return Object.fromEntries((gaps || []).map(g => [g.id, null]));
}

const MultipleChoiceExercise = ({
  mode = 'sentences',
  sentences: initialSentences,
  passage: initialPassage,
  gaps: initialGaps,
  title,
  level,
  onClose,
  updateAttributes,
  isStudent = false,
  isBoard = false,
  myState = {},
  otherState = {},
  updateState = () => {},
}) => {
  const items = mode === 'sentences' ? (initialSentences || []) : (initialGaps || []);

  const [answers, setAnswers] = useState(() => initAnswers(mode, initialSentences, initialGaps));
  const [results, setResults] = useState({});
  const [checked, setChecked] = useState(false);
  const [score, setScore] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableSentences, setEditableSentences] = useState(() => JSON.parse(JSON.stringify(initialSentences || [])));
  const [editableGaps, setEditableGaps] = useState(() => JSON.parse(JSON.stringify(initialGaps || [])));
  const [editablePassage, setEditablePassage] = useState(initialPassage || '');

  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);

  // Load initial state from myState or otherState once
  useEffect(() => {
    if (syncedRef.current) return;
    const src = Object.keys(myState).length > 0 ? myState
      : (Object.keys(otherState).length > 0 ? otherState : null);
    if (!src || !src.answers) return;
    syncedRef.current = true;
    if (src.answers) setAnswers(src.answers);
    if (src.results) setResults(src.results);
    if (typeof src.checked === 'boolean') setChecked(src.checked);
    if (src.score) setScore(src.score);
  }, [myState, otherState]); // eslint-disable-line

  // Sync answers from other side
  useEffect(() => {
    if (!otherState.answers) return;
    setAnswers(otherState.answers);
  }, [otherState.answers]); // eslint-disable-line

  // Sync results/checked/score from other side
  useEffect(() => {
    if (!otherState.results) return;
    setResults(otherState.results);
    if (typeof otherState.checked === 'boolean') setChecked(otherState.checked);
    if (otherState.score) setScore(otherState.score);
  }, [otherState.results]); // eslint-disable-line

  // Sync reset from other side
  useEffect(() => {
    if (otherState.resetAt && otherState.resetAt !== prevResetAtRef.current) {
      prevResetAtRef.current = otherState.resetAt;
      doReset(true);
    }
  }, [otherState.resetAt]); // eslint-disable-line

  const handleSelect = (id, optionIdx) => {
    if (checked) return;
    const newAnswers = { ...answers, [id]: optionIdx };
    setAnswers(newAnswers);
    updateState({ answers: newAnswers });
  };

  const handleCheck = () => {
    const newResults = {};
    let correct = 0;
    items.forEach(item => {
      const isCorrect = answers[item.id] === item.correct;
      newResults[item.id] = isCorrect ? 'correct' : 'incorrect';
      if (isCorrect) correct++;
    });
    const scoreData = { correct, total: items.length };
    setResults(newResults);
    setChecked(true);
    setScore(scoreData);
    updateState({ answers, results: newResults, checked: true, score: scoreData });
  };

  const doReset = (fromSync = false) => {
    const newAnswers = initAnswers(mode, initialSentences, initialGaps);
    setAnswers(newAnswers);
    setResults({});
    setChecked(false);
    setScore(null);
    if (!fromSync) {
      updateState({ answers: newAnswers, results: {}, checked: false, score: null, resetAt: Date.now() });
    }
  };

  const handleSaveEdit = () => {
    if (updateAttributes) {
      if (mode === 'sentences') {
        // Normalize underscore runs to the canonical `___` and clamp `correct`
        // to the current option count so neither bad input nor a stale index
        // can persist into the saved item.
        const sanitized = editableSentences.map(s => {
          const optCount = Array.isArray(s.options) ? s.options.length : 4;
          return {
            ...s,
            text: normalizeBlanks(s.text),
            correct: Math.min(Math.max(0, s.correct ?? 0), Math.max(0, optCount - 1)),
          };
        });
        updateAttributes({ sentences: sanitized, title: editableTitle, level });
      } else {
        const sanitized = editableGaps.map(g => {
          const optCount = Array.isArray(g.options) ? g.options.length : 4;
          return {
            ...g,
            correct: Math.min(Math.max(0, g.correct ?? 0), Math.max(0, optCount - 1)),
          };
        });
        updateAttributes({ passage: editablePassage, gaps: sanitized, title: editableTitle, level });
      }
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditableSentences(JSON.parse(JSON.stringify(initialSentences || [])));
    setEditableGaps(JSON.parse(JSON.stringify(initialGaps || [])));
    setEditablePassage(initialPassage || '');
    setEditableTitle(title);
    setIsEditing(false);
  };

  const allAnswered = items.every(item => answers[item.id] !== null && answers[item.id] !== undefined);

  // Results panel data — same ring/tier/headline pattern as ABCD's results
  // screen, just re-skinned with the teal accent this exercise already uses.
  let resultsData = null;
  if (score) {
    const total = score.total || 0;
    const percent = total ? Math.round((score.correct / total) * 100) : 0;
    const incorrect = total - score.correct;
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'practice';
    let headline;
    let subline;
    if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, incorrect, circumference, dashOffset, tier, headline, subline };
  }

  // Parse passage text into segments: string | { gapNum: number }
  const parsePassage = (text) => {
    const segments = [];
    const regex = /\[(\d+)\]/g;
    let last = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) segments.push(text.slice(last, match.index));
      segments.push({ gapNum: parseInt(match[1], 10) });
      last = match.index + match[0].length;
    }
    if (last < text.length) segments.push(text.slice(last));
    return segments;
  };

  const renderPassage = () => {
    const passageText = initialPassage || '';
    const segments = parsePassage(passageText);
    return segments.map((seg, i) => {
      if (typeof seg === 'string') return <span key={i}>{seg}</span>;
      const gapIndex = seg.gapNum - 1;
      const gap = (initialGaps || [])[gapIndex];
      if (!gap) return <span key={i} className="mc-gap-marker">[{seg.gapNum}]</span>;
      const selectedIdx = answers[gap.id];
      const result = results[gap.id];
      const hasAnswer = selectedIdx !== null && selectedIdx !== undefined;
      let markerClass = 'mc-gap-marker';
      if (result === 'correct') markerClass += ' correct';
      else if (result === 'incorrect') markerClass += ' incorrect';
      else if (hasAnswer) markerClass += ' answered';
      return (
        <span key={i} className={markerClass}>
          <span className="mc-gap-number">{seg.gapNum}</span>
          <span className="mc-gap-selected">
            {hasAnswer ? gap.options[selectedIdx] : '___'}
          </span>
        </span>
      );
    });
  };

  const renderSentenceText = (sentence, id) => {
    // Tolerate any underscore count — teachers type `_`, `___`, or `_____`,
    // and all of them mean "blank goes here". Split on the first run only.
    const match = (sentence || '').match(/_+/);
    const selectedIdx = answers[id];
    const result = results[id];
    const hasAnswer = selectedIdx !== null && selectedIdx !== undefined;
    let blankClass = 'mc-blank';
    if (result === 'correct') blankClass += ' correct';
    else if (result === 'incorrect') blankClass += ' incorrect';
    else if (hasAnswer) blankClass += ' filled';

    if (!match) {
      return <span>{sentence}</span>;
    }
    const before = sentence.slice(0, match.index);
    const after = sentence.slice(match.index + match[0].length);
    return (
      <>
        <span>{before}</span>
        <span className={blankClass}>
          {hasAnswer
            ? (mode === 'sentences' ? (initialSentences || []).find(s => s.id === id)?.options[selectedIdx] : '?')
            : '      '}
        </span>
        <span>{after}</span>
      </>
    );
  };

  // Edit mode for sentences
  const renderEditSentences = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {editableSentences.map((s, idx) => (
        <div key={s.id} className="mc-edit-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 20 }}>{idx + 1}.</span>
            <input
              data-mc-sentence-input={s.id}
              className="mc-edit-input"
              value={s.text}
              onChange={e => {
                const updated = [...editableSentences];
                updated[idx] = { ...s, text: e.target.value };
                setEditableSentences(updated);
              }}
              placeholder="Type your sentence. Use _ where the blank goes (any amount works)…"
            />
            <button
              type="button"
              className="mc-edit-insert-blank-btn"
              title="Insert blank at cursor"
              onClick={() => {
                const el = document.querySelector(`input[data-mc-sentence-input="${s.id}"]`);
                if (!el) return;
                const start = el.selectionStart ?? el.value.length;
                const end = el.selectionEnd ?? el.value.length;
                const next = el.value.slice(0, start) + '___' + el.value.slice(end);
                const updated = [...editableSentences];
                updated[idx] = { ...s, text: next };
                setEditableSentences(updated);
                requestAnimationFrame(() => {
                  el.focus();
                  const pos = start + 3;
                  el.setSelectionRange(pos, pos);
                });
              }}
            >+ blank</button>
            <input
              className="mc-edit-input"
              value={s.explanation || ''}
              onChange={e => {
                const updated = [...editableSentences];
                updated[idx] = { ...s, explanation: e.target.value };
                setEditableSentences(updated);
              }}
              placeholder="Explanation (optional)..."
              style={{ maxWidth: 200 }}
            />
            <button
              className="mc-edit-remove-btn"
              onClick={() => setEditableSentences(editableSentences.filter((_, i) => i !== idx))}
            >×</button>
          </div>
          <div className="mc-edit-option-count">
            <span className="mc-edit-option-count-label">Options:</span>
            {OPTION_COUNTS.map(n => (
              <button
                key={n}
                type="button"
                className={`mc-edit-option-count-btn${(s.options?.length || 4) === n ? ' is-active' : ''}`}
                onClick={() => {
                  const nextOpts = resizeOptions(s.options, n);
                  const nextCorrect = Math.min(s.correct ?? 0, n - 1);
                  const updated = [...editableSentences];
                  updated[idx] = { ...s, options: nextOpts, correct: nextCorrect };
                  setEditableSentences(updated);
                }}
              >{n}</button>
            ))}
          </div>
          <div className="mc-edit-options-group">
            {(s.options || ['', '', '', '']).map((opt, oi) => (
              <div key={oi} className="mc-edit-option-row">
                <label className="mc-edit-correct-radio">
                  <input
                    type="radio"
                    name={`correct-${s.id}`}
                    checked={s.correct === oi}
                    onChange={() => {
                      const updated = [...editableSentences];
                      updated[idx] = { ...s, correct: oi };
                      setEditableSentences(updated);
                    }}
                  />
                  <span className="mc-option-label">{OPTION_LABELS[oi]}</span>
                </label>
                <input
                  className="mc-edit-input"
                  value={opt}
                  onChange={e => {
                    const updated = [...editableSentences];
                    const newOptions = [...(s.options || ['', '', '', ''])];
                    newOptions[oi] = e.target.value;
                    updated[idx] = { ...s, options: newOptions };
                    setEditableSentences(updated);
                  }}
                  placeholder={`Option ${OPTION_LABELS[oi]}...`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        className="mc-add-btn"
        onClick={() => setEditableSentences([...editableSentences, {
          id: Date.now(),
          text: '',
          options: ['', '', '', ''],
          correct: 0,
          explanation: '',
        }])}
      >+ Add sentence</button>
    </div>
  );

  // Edit mode for text/passage
  const renderEditText = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div className="mc-passage-hint">
          <svg className="mc-passage-hint-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 7.25V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="8" cy="4.75" r="0.9" fill="currentColor"/>
          </svg>
          <span>Mark where each gap goes by typing <strong>[1]</strong>, <strong>[2]</strong>, <strong>[3]</strong>… in the text — gap 1 below fills in [1], gap 2 fills in [2], and so on.</span>
        </div>
        <textarea
          className="mc-edit-textarea"
          value={editablePassage}
          onChange={e => setEditablePassage(e.target.value)}
          rows={6}
          placeholder="Write the passage here, e.g. 'She [1] to the store yesterday.'"
        />
      </div>
      <div>
        <div className="mc-edit-label">Gaps</div>
        {editableGaps.map((g, idx) => (
          <div key={g.id} className="mc-edit-row" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0d9488', minWidth: 60 }}>Gap {idx + 1}</span>
              <input
                className="mc-edit-input"
                value={g.explanation || ''}
                onChange={e => {
                  const updated = [...editableGaps];
                  updated[idx] = { ...g, explanation: e.target.value };
                  setEditableGaps(updated);
                }}
                placeholder="Explanation (optional)..."
              />
              <button
                className="mc-edit-remove-btn"
                onClick={() => setEditableGaps(editableGaps.filter((_, i) => i !== idx))}
              >×</button>
            </div>
            <div className="mc-edit-option-count">
              <span className="mc-edit-option-count-label">Options:</span>
              {OPTION_COUNTS.map(n => (
                <button
                  key={n}
                  type="button"
                  className={`mc-edit-option-count-btn${(g.options?.length || 4) === n ? ' is-active' : ''}`}
                  onClick={() => {
                    const nextOpts = resizeOptions(g.options, n);
                    const nextCorrect = Math.min(g.correct ?? 0, n - 1);
                    const updated = [...editableGaps];
                    updated[idx] = { ...g, options: nextOpts, correct: nextCorrect };
                    setEditableGaps(updated);
                  }}
                >{n}</button>
              ))}
            </div>
            <div className="mc-edit-options-group">
              {(g.options || ['', '', '', '']).map((opt, oi) => (
                <div key={oi} className="mc-edit-option-row">
                  <label className="mc-edit-correct-radio">
                    <input
                      type="radio"
                      name={`gcorrect-${g.id}`}
                      checked={g.correct === oi}
                      onChange={() => {
                        const updated = [...editableGaps];
                        updated[idx] = { ...g, correct: oi };
                        setEditableGaps(updated);
                      }}
                    />
                    <span className="mc-option-label">{OPTION_LABELS[oi]}</span>
                  </label>
                  <input
                    className="mc-edit-input"
                    value={opt}
                    onChange={e => {
                      const updated = [...editableGaps];
                      const newOptions = [...(g.options || ['', '', '', ''])];
                      newOptions[oi] = e.target.value;
                      updated[idx] = { ...g, options: newOptions };
                      setEditableGaps(updated);
                    }}
                    placeholder={`Option ${OPTION_LABELS[oi]}...`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button
          className="mc-add-btn"
          onClick={() => setEditableGaps([...editableGaps, {
            id: Date.now(),
            options: ['', '', '', ''],
            correct: 0,
            explanation: '',
          }])}
        >+ Add gap</button>
      </div>
    </div>
  );

  return (
    <div className="mc-exercise">
      {/* Header */}
      <div className="mc-header">
        <div className="mc-header-left">
          <div className="mc-header-icon"><MCIcon /></div>
          {isEditing ? (
            <input
              className="mc-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="mc-title">{title || 'Multiple Choice Exercise'}</span>
          )}
          <span className="mc-level-badge">{level || 'B1'}</span>
        </div>
        <div className="mc-header-actions">
          {!isStudent && !isEditing && !checked && (
            <button className="mc-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          )}
          <button className="mc-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
              <circle cx="12" cy="12" r="3" className="eye-pupil" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="mc-body">
        {/* Edit mode */}
        {isEditing && (
          mode === 'sentences' ? renderEditSentences() : renderEditText()
        )}

        {/* Play mode — sentences */}
        {!isEditing && mode === 'sentences' && (
          <div>
            {(initialSentences || []).map((s, idx) => {
              const result = results[s.id];
              const selectedIdx = answers[s.id];
              return (
                <div key={s.id} className="mc-sentence-item">
                  <div className="mc-sentence-text">
                    <span style={{ fontWeight: 600, color: '#64748b', marginRight: 8 }}>{idx + 1}.</span>
                    {renderSentenceText(s.text, s.id)}
                  </div>
                  <div className="mc-options">
                    {(s.options || []).map((opt, oi) => {
                      const isSelected = selectedIdx === oi;
                      const isCorrectOpt = s.correct === oi;
                      let btnClass = 'mc-option-btn';
                      if (checked) {
                        if (isCorrectOpt) btnClass += ' correct-indicator';
                        else if (isSelected && !isCorrectOpt) btnClass += ' wrong-indicator';
                      } else if (isSelected) {
                        btnClass += ' selected';
                      }
                      return (
                        <button
                          key={oi}
                          className={btnClass}
                          onClick={() => handleSelect(s.id, oi)}
                          disabled={checked}
                        >
                          <span className="mc-option-label">{OPTION_LABELS[oi]}</span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {checked && result === 'incorrect' && s.explanation && (
                    <div className="mc-explanation">{s.explanation}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Play mode — text */}
        {!isEditing && mode === 'text' && (
          <div>
            <div className="mc-passage">
              {renderPassage()}
            </div>
            {(initialGaps || []).map((g, idx) => {
              const result = results[g.id];
              const selectedIdx = answers[g.id];
              return (
                <div key={g.id} className="mc-gap-group">
                  <div className="mc-gap-label">Gap {idx + 1}</div>
                  <div className="mc-options">
                    {(g.options || []).map((opt, oi) => {
                      const isSelected = selectedIdx === oi;
                      const isCorrectOpt = g.correct === oi;
                      let btnClass = 'mc-option-btn';
                      if (checked) {
                        if (isCorrectOpt) btnClass += ' correct-indicator';
                        else if (isSelected && !isCorrectOpt) btnClass += ' wrong-indicator';
                      } else if (isSelected) {
                        btnClass += ' selected';
                      }
                      return (
                        <button
                          key={oi}
                          className={btnClass}
                          onClick={() => handleSelect(g.id, oi)}
                          disabled={checked}
                        >
                          <span className="mc-option-label">{OPTION_LABELS[oi]}</span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {checked && result === 'incorrect' && g.explanation && (
                    <div className="mc-explanation">{g.explanation}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Results panel */}
        {resultsData && (
          <div className={`mc-results tier-${resultsData.tier}`}>
            <div className="mc-results-ring-wrap">
              <svg viewBox="0 0 100 100" className="mc-results-ring" aria-hidden>
                <circle cx="50" cy="50" r="42" className="mc-results-ring-track" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="mc-results-ring-progress"
                  style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                />
              </svg>
              <div className="mc-results-ring-center">
                <div className="mc-results-percent">{resultsData.percent}%</div>
                <div className="mc-results-percent-label">score</div>
              </div>
            </div>
            <div className="mc-results-headline">{resultsData.headline}</div>
            <div className="mc-results-subline">{resultsData.subline}</div>
            <div className="mc-results-stats">
              <div className="mc-results-stat is-correct">
                <span className="mc-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span className="mc-results-stat-value">{score.correct}</span>
                <span className="mc-results-stat-label">correct</span>
              </div>
              <div className="mc-results-stats-divider" aria-hidden />
              <div className="mc-results-stat is-incorrect">
                <span className="mc-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </span>
                <span className="mc-results-stat-value">{resultsData.incorrect}</span>
                <span className="mc-results-stat-label">incorrect</span>
              </div>
            </div>
            <button className="mc-results-cta" onClick={() => doReset()}>
              <span>Try Again</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Footer — hidden once results are showing; the "Try Again" CTA
          lives inside the results panel instead, same as ABCD. */}
      {(isEditing || !checked) && (
        <div className="mc-footer">
          {isEditing ? (
            <>
              <button className="mc-cancel-btn" onClick={handleCancelEdit}>Cancel</button>
              <button className="mc-save-btn" onClick={handleSaveEdit}>Save</button>
            </>
          ) : (
            <button
              className="mc-check-btn"
              onClick={handleCheck}
              disabled={!allAnswered}
            >
              Check Answers
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MultipleChoiceExercise;
